import { computed, defineComponent } from "vue";
import { mapActions } from "pinia";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";

import {
    formatDate,
    formatScheduledTime,
    formatTimeInOut
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import {
    getApplicationId,
    getProductFullName
} from "../../../../../common/vue-helpers/modifiers/application-modifiers";
import { ApplicationEnum } from "../../../../../enums/ApplicationEnum";
import {
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    VisitStatusEnum
} from "../../../../../enums/enums";
import { IVisitDetails } from "../../../../../resources-vue/vue-interfaces/i-visit-details";
import { IReassignVisitDetails } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { TaskService, type ITaskByUserResponse } from "../../../../../services/task.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { PatientService } from "../../../../../services/patient.service";
import { NavigationService } from "../../../../../services/navigation.service";
import { AnalyticsService, AnalyticsCategory } from "../../../../../services/analytics.service";
import { BulkReassign } from "../../../../../resources-vue/vue-models/bulk-reassign";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";
import { ScheduleLegendState } from "../../../../../pages/central-calendar/central-calendar";

import ProductPill from "../../../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";
import SortableTh from "../../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import EvvDetails from "../../../../../resources-vue/vue-dialogs/EvvDetails/EvvDetails.vue";
import ReassignVisit from "../../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import VisitDetails from "../../../../../resources-vue/vue-dialogs/VisitDetails/VisitDetails.vue";
import PatientProfile from "../../../../../resources-vue/vue-dialogs/PatientProfile/PatientProfile.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";

export default defineComponent({
    components: {
        ProductPill,
        SortableTh,
        EvvDetails
    },

    emits: ["calendarRefresh"],

    props: {
        summary: {
            default: null
        },
        columns: {
            default: 11,
            type: Number
        }
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    data(vm) {
        return {
            isViewScheduleRequested: false as boolean,
            viewScheduleId: "" as string,
            formatDate,
            getProductFullName,
            getApplicationId,
            formatScheduledTime,
            formatTimeInOut,
            ApplicationEnum,
            sortBy: "startDate",
            sortOrder: "desc" as "asc" | "desc",
            dialogRef: null,
            _permissionManager: null,
            _taskService: null,
            _toastrService: null,
            _patientsService: null,
            _navigationService: null,
            _analyticsService: null,
            isReassignAllowed: false as boolean,
            isEditVisitDetailsAllowed: false as boolean,
            showEditBtn: false as boolean,
            patientDetails: null,
            application: ""
        };
    },

    created() {
        this._permissionManager = Container.instance.get(PermissionManager);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._patientsService = Container.instance.get(PatientService);
        this._navigationService = Container.instance.get(NavigationService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
        this.isEditVisitDetailsAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.scheduler,
            PermissionActionEnum.canEdit
        );
        this.showEditBtn = this.isReassignAllowed || this.isEditVisitDetailsAllowed;
    },

    computed: {
        sortedSchedules() {
            const direction = this.sortOrder === "asc" ? 1 : -1;

            const sortedData = [...this.summary?.schedules].sort((a, b) => {
                const first = this.getSortValue(a, this.sortBy);
                const second = this.getSortValue(b, this.sortBy);

                if (typeof first === "number") {
                    return direction * (first - second);
                }

                return (
                    direction *
                    String(first ?? "")
                        .trim()
                        .localeCompare(String(second ?? "").trim(), undefined, { sensitivity: "base" })
                );
            });

            return sortedData;
        },

        schedulesIncludesHHOrHCVisit() {
            return this.summary?.schedules.some(
                (schedule) =>
                    schedule.Application === ApplicationEnum.AgencyCore ||
                    schedule.Application === ApplicationEnum.HomeCare
            );
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),

        canReassign(schedule: ITaskByUserResponse) {
            return (
                this.isReassignAllowed &&
                !schedule.IsNonVisitAssignment &&
                (schedule.Status == VisitStatusEnum.NotStarted || schedule.Status == VisitStatusEnum.NotYetDue) &&
                (schedule as any).EvvIconStatus !== EvvStatusHomeCareEnum.Finished &&
                (schedule as any).EvvIconStatus !== EvvStatusHomeCareEnum.FinishedAdjusted &&
                (schedule as any).EvvIconStatus !== EvvStatusHomeCareEnum.InProgress &&
                (schedule as any).EvvIconStatus !== EvvStatusHomeCareEnum.InProgressAdjusted &&
                (schedule as any).VerificationVisitStatus !== EvvStatusHomeHealthEnum.InProgress &&
                (schedule as any).VerificationVisitStatus !== EvvStatusHomeHealthEnum.PatientVerified &&
                (schedule as any).VerificationVisitStatus !== EvvStatusHomeHealthEnum.ClinicianVerified
            );
        },

        canViewDetails(schedule: ITaskByUserResponse) {
            return this.isEditVisitDetailsAllowed;
        },

        canViewProfile(schedule: ITaskByUserResponse) {
            return this.showEditBtn && !schedule.IsNonVisitAssignment;
        },

        canDelete(schedule: ITaskByUserResponse) {
            return (
                this.showEditBtn &&
                (schedule.Status == VisitStatusEnum.NotStarted ||
                    schedule.Status == VisitStatusEnum.NotYetDue ||
                    schedule.IsNonVisitAssignment)
            );
        },

        canGoToPatientChart(schedule: ITaskByUserResponse) {
            return (schedule as any).State !== ScheduleLegendState["non-visit-bg"];
        },

        hasAnyAction(schedule: ITaskByUserResponse) {
            return (
                this.showEditBtn &&
                (this.canReassign(schedule) ||
                    this.canViewDetails(schedule) ||
                    this.canViewProfile(schedule) ||
                    this.canGoToPatientChart(schedule) ||
                    this.canDelete(schedule))
            );
        },

        async scheduledVisit(schedule: ITaskByUserResponse) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Employee calendar reassign visit"
            });
            const additionalInfo = {
                FirstName: schedule.PatientFirstName,
                LastName: schedule.PatientLastName,
                UserFirstName: this.summary?.firstName,
                UserLastName: this.summary?.lastName
            };
            let visitDetails: any = Object.assign({}, additionalInfo, schedule);
            visitDetails.Application = visitDetails.Application == -3 ? 256 : visitDetails.Application;
            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        minWidth: "650px",
                        minHeight: visitDetails.Application === ApplicationEnum.HomeCare ? "350px" : "280px"
                    }
                },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.$emit("calendarRefresh");
                    }
                }
            });
        },

        async showVisitDetails(schedule: ITaskByUserResponse) {
            const visitDetails: any = Object.assign({}, schedule);
            visitDetails.isEmployeeCalendar = true;
            if (visitDetails.Application == ApplicationEnum.AxxessHospice) {
                visitDetails.Application = 256;
            }
            this.dialogRef = this.$dialog.open(VisitDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: visitDetails
                },
                onClose: (response) => {
                    if (response.data === "success") {
                        this.$emit("calendarRefresh");
                    }
                }
            });
        },

        async openProfile(schedule: ITaskByUserResponse) {
            try {
                const data = Object.assign({}, schedule) as any;
                const payload = {
                    agencyId: data.AgencyId,
                    application:
                        data.Application === ApplicationEnum.AxxessHospice
                            ? ApplicationEnum.AxxessHospiceFE
                            : data.Application,
                    patientId: data.PatientId
                };
                this.application = data.Application;
                const response = await this._patientsService.getPatientsProfile(payload);
                if (response.IsSuccessful) {
                    this.patientDetails = response.data;
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                    });
                }
            } catch (err) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                });
            }

            const patientInfo = (({ BranchName: AgencyName, AgencyId, Application }) => ({
                AgencyName,
                AgencyId,
                Application
            }))(schedule as any);
            const patientData: BulkReassign = Object.assign({}, this.patientDetails, patientInfo);
            this.dialogRef = this.$dialog.open(PatientProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 750px"
                },
                data: {
                    patientData
                }
            });
        },

        async handlePatientNameClick(schedule: ITaskByUserResponse) {
            let redirectResponse;
            let action = "";
            const taskDetails = schedule as any;

            if (taskDetails.Application === ApplicationEnum.AxxessHospice) {
                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(taskDetails.AgencyId)}/patients/patient-chart/${taskDetails.PatientId}?accountId=${taskDetails.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
            } else {
                const isClinician = this._permissionManager.checkPermission(
                    ParentPermissionEnum.clinician,
                    PermissionActionEnum.canView
                );
                this.showLoading(true);
                redirectResponse = await this._navigationService.navigateToPatient({
                    agencyId: taskDetails.AgencyId,
                    applicationId: taskDetails.Application,
                    isClinician: isClinician,
                    patientId: taskDetails.PatientId
                });
                action = taskDetails.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
                this.showLoading(false);
            }

            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },

        async deleteTask(schedule: ITaskByUserResponse) {
            try {
                let promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete selected task?",
                    okText: "Yes",
                    cancelText: "No"
                };
                const eventId = [schedule.Id] as string[];

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },

                    data: {
                        promptOptions
                    },

                    onClose: async (options: any) => {
                        if (options.data == "success") {
                            this.showLoading(true);
                            try {
                                const reassignPayload =
                                    schedule.Application == ApplicationEnum.AgencyCore
                                        ? {
                                              AgencyId: schedule.AgencyId,
                                              Application: schedule.Application,
                                              PatientId: schedule.PatientId,
                                              EpisodeId: schedule.EpisodeId,
                                              EventId: eventId,
                                              IsPatient: true
                                          }
                                        : {
                                              AgencyId: schedule.AgencyId,
                                              Application:
                                                  schedule.Application == ApplicationEnum.AxxessHospice
                                                      ? ApplicationEnum.AxxessHospiceFE
                                                      : schedule.Application,
                                              PatientId: schedule.PatientId,
                                              EventId: eventId,
                                              IsPatient: true
                                          };
                                const res = await this._taskService.patientBulkDelete(reassignPayload);
                                if (!res.isSuccessful) {
                                    throw Error;
                                }
                                this.$emit("calendarRefresh");
                                this.showLoading(false);
                                this._toastrService.success({
                                    message: `The task has been successfully deleted.`,
                                    title: `Task Deleted`
                                });
                            } catch (error) {
                                this.showLoading(false);
                                this._toastrService.error({
                                    title: `Error - Deleting task`,
                                    message: `There was an error deleting the visit. Please Try again later.`
                                });
                            }
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Task`,
                    message: `There was a problem while deleting the tasks. Please try again.`
                });
            }
        },

        getSortValue(item: any, field: string) {
            switch (field) {
                case "taskName":
                    return item.TaskName;

                case "agencyName":
                    return item.AgencyName;

                case "branchName":
                    return item.BranchName;

                case "patientName":
                    return this.getPatientName(item);

                case "startDate":
                    return moment(item.StartDate).valueOf();

                default:
                    return "";
            }
        },
        handleViewSchedule(summary: any) {
            this.isViewScheduleRequested = true;
            this.viewScheduleId = summary.id;
        },
        handleCloseViewSchedule() {
            this.isViewScheduleRequested = false;
            this.viewScheduleId = "";
        },
        getPatientName(schedule) {
            if (!schedule.PatientLastName && !schedule.PatientFirstName) {
                return "Unassigned";
            } else {
                return `${schedule.PatientLastName}, ${schedule.PatientFirstName}`;
            }
        },
        getTime(schedule) {
            return schedule.Application === ApplicationEnum.HomeCare ||
                schedule.Application === ApplicationEnum.AgencyCore
                ? formatScheduledTime(schedule.StartTime) !== formatScheduledTime(schedule.EndTime)
                    ? formatTimeInOut(formatScheduledTime(schedule.StartTime), formatScheduledTime(schedule.EndTime))
                    : ""
                : formatTimeInOut(formatScheduledTime(schedule.StartTime), formatScheduledTime(schedule.EndTime));
        },
        getHour(schedule) {
            const time = this.getTime(schedule);
            if (time === "") {
                return 0;
            } else {
                const [startStr, endStr] = time.split(" - ");
                const start = moment(startStr, "hh:mm A");
                const end = moment(endStr, "hh:mm A");
                if (end.isBefore(start)) {
                    end.add(1, "day"); // handle next day
                }
                const duration = moment.duration(end.diff(start));
                return `${duration.asHours().toFixed(2)}`;
            }
        },

        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
            }
        },

        sortOrderUpdated(value: "asc" | "desc") {
            if (value != this.sortOrder) {
                this.sortOrder = value;
            }
        },

        getEvvIconClass(schedule) {
            if (schedule.Application == ApplicationEnum.HomeCare) {
                if (
                    schedule.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    schedule.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    return schedule.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                        ? "evv-adjusted signed"
                        : "fa fa-home fa-md signed";
                } else if (
                    schedule.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    schedule.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    return schedule.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                        ? "evv-adjusted in-progress"
                        : "fa fa-home fa-md";
                } else if (schedule.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    return "fa fa-home fa-md not-started";
                }
                return "";
            } else if (schedule.Application == ApplicationEnum.AgencyCore) {
                if (schedule.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    return "fa fa-home fa-md";
                } else if (
                    schedule.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    schedule.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    return "fa fa-home fa-md signed";
                } else if (schedule.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    return "fa fa-home fa-md not-started";
                }
                return "";
            } else {
                return "";
            }
        },

        getEvvButtonClass(schedule) {
            if (schedule.Application == ApplicationEnum.HomeCare) {
                if (schedule.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    return "evv gray-house";
                }
                return "evv";
            } else if (schedule.Application == ApplicationEnum.AgencyCore) {
                if (schedule.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    return "evv ps-1";
                } else if (schedule.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    return "evv gray-house";
                }
                return "evv";
            } else {
                return "evv";
            }
        },

        openEvvHouse(schedule: any) {
            const visitDetails: IVisitDetails = Object.assign({}, schedule);
            visitDetails.isEmployeeCalendar = true;
            this.dialogRef = this.$dialog.open(EvvDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: visitDetails
                }
            });
        }
    }
});
